import { ApolloClient, InMemoryCache, HttpLink, NormalizedCacheObject, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';

// Public GraphQL endpoint
const endpoint = process.env.NEXT_PUBLIC_API_ENDPOINT || 'http://localhost';

// Public GraphQL link with Cache-Control header
const publicLink = new HttpLink({
  uri: `${endpoint}/public/opengraphql`,
  headers: {
    'Cache-Control': 'no-cache'
  }
});

// Authenticated GraphQL link
const authLink = new HttpLink({
  uri: `${endpoint}/graphql`
});

// Middleware to set Cache-Control and auth headers
const authMiddleware = setContext((_, {
  headers
}) => {
  const token = localStorage.getItem('token'); // Retrieve token from localStorage or other storage mechanism
  return {
    headers: {
      ...headers,
      "x-ugeo-token": token ? `Bearer ${token}` : '',
      'Cache-Control': 'no-cache' // Add Cache-Control header here
    }
  };
});

// Retry only for certain status codes, skip 429
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 3,
    retryIf: (error, _operation) => {
      const statusCode = error?.statusCode || error?.response?.status;
      // Skip retry if 429
      if (statusCode === 429) {
        return false;
      }
      // Retry for network errors or 5xx responses
      return !!error && (statusCode >= 500 || error.networkError);
    }
  }
});
const errorLink = onError(({
  graphQLErrors,
  networkError
}) => {
  if (graphQLErrors) graphQLErrors.forEach(({
    message,
    locations,
    path
  }) => console.error(`[GraphQL error]: ${message}`, {
    locations,
    path
  }));
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

// Create Apollo Client instance
export const createApolloClient = (authenticated: boolean): ApolloClient<NormalizedCacheObject> => {
  const link: any = authenticated ? from([errorLink, retryLink, authMiddleware, authLink]) : from([errorLink, retryLink, publicLink]);
  return new ApolloClient({
    link,
    cache: new InMemoryCache()
  });
};